import { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { api } from '../hooks/useAuth';

export default function ConnectAccounts() {
  const [status, setStatus] = useState(null);
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState('');
  const [igAccount, setIgAccount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchStatus();
    const params = new URLSearchParams(window.location.search);
    if (params.get('fb_connected')) {
      setMessage({ type: 'success', text: 'Facebook connected! Now select your page below.' });
      loadPages();
      window.history.replaceState({}, '', '/connect');
    }
    if (params.get('fb_error')) {
      setMessage({ type: 'error', text: 'Facebook connection failed. Please try again.' });
      window.history.replaceState({}, '', '/connect');
    }
  }, []);

  const fetchStatus = async () => {
    try {
      const { data } = await api.get('/api/auth/facebook/status');
      setStatus(data);
      if (data.fb_connected && !data.ig_connected) {
        await loadPages();
      }
    } catch (_) {}
  };

  const loadPages = async () => {
    try {
      const { data } = await api.get('/api/auth/facebook/pages');
      setPages(data.accounts || []);
    } catch (e) {
      setMessage({ type: 'error', text: 'Failed to load pages' });
    }
  };

  const connectFacebook = () => {
    const token = localStorage.getItem('vc_token');
    window.location.href = `${process.env.REACT_APP_BASE_URL}/api/auth/facebook?token=${token}`;
  };

  const savePage = async () => {
    if (!selectedPage) return;
    setLoading(true);
    try {
      await api.post('/api/auth/facebook/save-page', { selectedAccount: selectedPage });
      const { data } = await api.get('/api/auth/facebook/instagram');
      setIgAccount(data);
      setMessage({ type: 'success', text: `Instagram @${data.instagram_username} connected!` });
      fetchStatus();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.error || 'Failed to link Instagram' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-layout">
      <Sidebar />
      <main className="main-content">
        <div className="page-header">
          <div>
            <h1 className="page-title">🔐 OAuth Connect</h1>
            <p className="page-subtitle">One-click Facebook & Instagram connection (Recommended)</p>
          </div>
        </div>

        {message && (
          <div className={`alert alert-${message.type}`} style={{ marginBottom: 24 }}>
            {message.text}
          </div>
        )}

        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <div>
              <h3 style={{ fontSize: 16, marginBottom: 4 }}>Instagram Business</h3>
              <p style={{ fontSize: 13, color: 'var(--text-dim)' }}>Automatic OAuth authorization</p>
            </div>
            {status?.ig_connected
              ? <span className="badge badge-success">✓ Connected</span>
              : <span className="badge badge-danger">Not connected</span>}
          </div>

          {/* Prerequisites Guide Box */}
          <div style={{ 
            padding: '14px 16px', 
            background: 'var(--bg)', 
            borderRadius: 10, 
            marginBottom: 20,
            fontSize: 12,
            lineHeight: 1.6,
            border: '1px solid var(--border)'
          }}>
            <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--text)' }}>📋 Before you connect:</div>
            <ul style={{ margin: 0, paddingLeft: 20, color: 'var(--text-dim)' }}>
              <li>Ensure your Instagram account is set to a <strong>Business</strong> or <strong>Creator</strong> account (Personal accounts are not supported by Meta's API).</li>
              <li>Your Instagram account must be <strong>linked to a Facebook Page</strong> that you manage.</li>
              <li>When the Facebook popup opens, make sure to <strong>select the correct Facebook Page</strong> and <strong>linked Instagram Business account</strong>, and grant all requested permissions.</li>
            </ul>
          </div>

          {/* Step 1 */}
          <div style={{ marginBottom: 20, padding: '16px', background: 'var(--bg)', borderRadius: 12, border: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: status?.fb_connected ? 'rgba(6,214,160,0.2)' : 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>
                {status?.fb_connected ? '✓' : '1'}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>Connect Facebook</div>
                <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>Authorize access to your pages</div>
              </div>
              <button className="btn btn-secondary btn-sm" onClick={connectFacebook}>
                {status?.fb_connected ? 'Reconnect' : 'Connect →'}
              </button>
            </div>
          </div>

          {/* Step 2 */}
          {pages.length > 0 && (
            <div style={{ padding: '16px', background: 'var(--bg)', borderRadius: 12, border: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: status?.ig_connected ? 'rgba(6,214,160,0.2)' : 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>
                  {status?.ig_connected ? '✓' : '2'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>Select Facebook Page</div>
                  <select
                    className="form-select"
                    value={selectedPage}
                    onChange={(e) => setSelectedPage(e.target.value)}
                    style={{ marginBottom: 10 }}
                  >
                    <option value="">-- Select a page --</option>
                    {pages.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={savePage}
                    disabled={!selectedPage || loading}
                  >
                    {loading ? <span className="spinner" /> : 'Link Instagram →'}
                  </button>
                  {igAccount && (
                    <p style={{ fontSize: 13, color: 'var(--success)', marginTop: 8 }}>
                      ✓ Linked to @{igAccount.instagram_username}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="card" style={{ marginTop: 20, padding: '16px 20px', background: 'var(--bg3)', border: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 20 }}>💡</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>Need manual credentials?</div>
              <div style={{ fontSize: 13, color: 'var(--text-dim)' }}>
                For advanced users who prefer to enter Instagram Business ID and Access Token manually
              </div>
            </div>
            <a href="/manual-connect" className="btn btn-secondary btn-sm">
              Go to Manual Connect →
            </a>
          </div>
        </div>
      </main>
    </div>
  );
}